import ImageUploadField from "../../../../shared/components/ImageUploadField";
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { LeagueCreateInput, LeagueDetail, LeagueUpdateInput } from "@tnp/contracts";
import AppSelect from "../../../../shared/components/AppSelect";
import Button from "../../../../shared/components/Button";
import Switch from "../../../../shared/components/Switch";
import LeagueFeedback from "../../shared/components/LeagueFeedback";
import { getLeagueTexts } from "../../shared/leagueTexts";
import { parseMonthlyAmount } from "../../shared/utils/leagueFormatting";
import { getLeagueFlowTexts } from "../leagueFlowTexts";
import LeagueFlowSteps from "./LeagueFlowSteps";
import LeagueRuleCard from "./LeagueRuleCard";

type Props = {
  league?: LeagueDetail;
  onSave: (settings: LeagueCreateInput | LeagueUpdateInput) => Promise<void>;
};
export default function LeagueSettingsForm({ league, onSave }: Props) {
  const t = getLeagueTexts();
  const f = getLeagueFlowTexts();
  const [step, setStep] = useState(0);
  const [name, setName] = useState(league?.name || "");
  const [imageUrl, setImageUrl] = useState(league?.imageUrl || "");
  const [description, setDescription] = useState(league?.description || "");
  const [venue, setVenue] = useState(league?.venue || "");
  const [timezone, setTimezone] = useState(league?.timezone || "America/Sao_Paulo");
  const [year, setYear] = useState(league?.seasons[0]?.year || Number(new Intl.DateTimeFormat("en", { year: "numeric", timeZone: "America/Sao_Paulo" }).format(new Date())));
  const [genders, setGenders] = useState<Array<"male" | "female">>(league?.seasons[0]?.divisions.map(d => d.gender) || ["male", "female"]);
  const [amount, setAmount] = useState(((league?.monthlyAmountCents ?? 4000) / 100).toFixed(2).replace(".", ","));
  const [monthlyEnabled, setMonthlyEnabled] = useState((league?.monthlyAmountCents ?? 4000) > 0);
  const [dueDay, setDueDay] = useState(league?.dueDay ?? 10);
  const [deadline, setDeadline] = useState(league?.roundDeadlineDays ?? 15);
  const [rulesText, setRulesText] = useState(league?.rulesText || "");
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState("");
  const [status, setStatus] = useState<LeagueUpdateInput["status"]>(league?.status || "draft");
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef(step);
  useEffect(() => {
    if (previousStep.current !== step) headingRef.current?.focus();
    previousStep.current = step;
  }, [step]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setValidation("");
    if (step === 0) { setStep(1); return; }
    const monthlyAmountCents = monthlyEnabled ? parseMonthlyAmount(amount) : 0;
    if (monthlyAmountCents === null) { setValidation(t.moneyError); setStep(1); return; }
    if (!genders.length) { setValidation(t.divisionError); setStep(1); return; }
    if (step === 1) { setStep(2); return; }
    const common = { name, description, venue, imageUrl, timezone, monthlyAmountCents, dueDay, roundDeadlineDays: deadline, rulesText };
    setBusy(true);
    try {
      await onSave(league ? { ...common, revision: league.revision, status } : { ...common, year, genders });
    } finally { setBusy(false); }
  }

  const titles = [f.general, f.rules, f.review];
  return <form className="league-form league-setup" onSubmit={submit}>
    <LeagueFlowSteps current={step} />
    <section className="league-panel">
      <h3 ref={headingRef} tabIndex={-1}>{titles[step]}</h3>
      <p className="league-flow-caption">{[f.generalHint, f.rulesHint, league ? t.publishHint : f.reviewHint][step]}</p>
      <fieldset disabled={busy}>
        <fieldset hidden={step !== 0} disabled={step !== 0}>
          <label>{t.name}<input required minLength={3} maxLength={120} value={name} onChange={e => setName(e.target.value)} /></label>
          <div className="league-grid">
            <label>{t.year}<input type="number" required min={2020} max={2100} disabled={Boolean(league)} value={year} onChange={e => setYear(Number(e.target.value))} /></label>
            <label>{t.venue}<input maxLength={200} value={venue} onChange={e => setVenue(e.target.value)} /></label>
          </div>
          <ImageUploadField label={t.image} value={imageUrl} aspectRatio={16 / 9} outputWidth={1200} onChange={setImageUrl} onRemove={() => setImageUrl("")} />
          <label>{t.description}<textarea rows={3} maxLength={2000} value={description} onChange={e => setDescription(e.target.value)} /></label>
          <details className="league-roster-help"><summary>{f.advanced}</summary>
            <p>{t.venueHint}</p>
            <label>{t.timezone}<input required maxLength={80} value={timezone} onChange={e => setTimezone(e.target.value)} /></label>
          </details>
        </fieldset>
        <fieldset hidden={step !== 1} disabled={step !== 1}>
          <LeagueRuleCard title={t.divisions} icon="groups">
            {(["male", "female"] as const).map(gender => <Switch key={gender} label={t[gender]} checked={genders.includes(gender)} disabled={Boolean(league)} onChange={checked => setGenders(previous => checked ? [...previous, gender] : previous.filter(g => g !== gender))} />)}
            {league && <details className="league-roster-help"><summary>{t.divisions}</summary><p>{t.seasonHint}</p></details>}
          </LeagueRuleCard>
          <LeagueRuleCard title={f.fees} icon="payments">
            <Switch label={f.fees} checked={monthlyEnabled} onChange={setMonthlyEnabled} />
            {monthlyEnabled && <div className="league-grid">
              <label>{t.monthly}<input required inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} /></label>
              <label>{t.dueDay}<input required type="number" min={1} max={28} value={dueDay} onChange={e => setDueDay(Number(e.target.value))} /></label>
            </div>}
          </LeagueRuleCard>
          <LeagueRuleCard title={f.deadline} icon="calendar_month">
            <label>{t.deadline}<input required type="number" min={1} max={60} value={deadline} onChange={e => setDeadline(Number(e.target.value))} /></label>
          </LeagueRuleCard>
          <LeagueRuleCard title={t.rules} icon="description">
            <label>{t.rules}<textarea rows={6} maxLength={20000} value={rulesText} onChange={e => setRulesText(e.target.value)} /></label>
          </LeagueRuleCard>
        </fieldset>
        <div hidden={step !== 2}>
          <dl className="league-review-summary">
            <div><dt>{t.name}</dt><dd>{name}</dd></div>
            <div><dt>{t.year}</dt><dd>{year}</dd></div>
            <div><dt>{t.venue}</dt><dd>{venue || t.noVenue}</dd></div>
            <div><dt>{t.divisions}</dt><dd>{genders.map(gender => t[gender]).join(" · ")}</dd></div>
            <div><dt>{f.fees}</dt><dd>{monthlyEnabled ? `${t.monthly}: ${amount}` : f.noFees}</dd></div>
            <div><dt>{t.deadline}</dt><dd>{deadline}</dd></div>
          </dl>
          <details className="league-roster-help"><summary>{t.rules}</summary><p className="league-rules">{rulesText || t.missingRules}</p></details>
          {league && <label>{t.statusLabel}
            <AppSelect value={status} onChange={event => setStatus(event.target.value as LeagueUpdateInput["status"])}>
              <option value="draft">{t.draft}</option><option value="published">{t.published}</option><option value="archived">{t.archived}</option>
            </AppSelect>
          </label>}
        </div>
        {validation && <LeagueFeedback>{validation}</LeagueFeedback>}
        <div className="league-setup-actions">
          {step > 0 && <Button type="button" variant="ghost" onClick={() => { setValidation(""); setStep(value => value - 1); }}>{f.previous}</Button>}
          <Button type="submit">{busy ? t.saving : step < 2 ? f.next : league ? t.saveChanges : f.finish}</Button>
        </div>
      </fieldset>
    </section>
  </form>;
}
